import dagre from 'dagre';
import { Position, type Edge, type Node } from 'reactflow';
import type { GeneratedStack } from '../types';

/**
 * Layout engine for AI-generated (and Mermaid-imported) diagrams.
 *
 * Three strategies:
 * 1. Semantic placement — the model authors layout *judgment* as hints
 *    (layer / orderInLayer / mainPath on each node) and a deterministic
 *    placer turns them into coordinates with guaranteed spacing. The main
 *    path renders as a straight spine with side branches fanning out.
 * 2. Layered hierarchical pass — ranking derived from the edges themselves
 *    (longest-path layering, cycles broken deterministically). Authored
 *    layer hints pin ranks and win over the inferred ones. In-house: no
 *    elkjs or other layout dependency.
 * 3. Dagre auto-layout — fallback when hints are missing and grouping
 *    constraints need a compound layout.
 *
 * This mirrors the Archify finding: auto-layout + nice styling is not
 * meaningfully better than stock output; layout judgment IS the product.
 */

export type LayoutDirection = 'TB' | 'BT' | 'LR' | 'RL';

// Matches the dimensions dagre approximates for tech nodes.
const NODE_W = 220;
const NODE_H = 100;

interface FlatLayoutResult {
  nodes: Node[];
  edges: Edge[];
}

// Center-to-center spacing for semantic placement. Exported so callers (and
// tests) can reference the product's spacing baseline.
export const LAYER_STEP = NODE_H + 110; // along the flow axis
export const SLOT_STEP = NODE_W + 110; // across the flow axis

export interface GroupRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Group spec consumed by the layout entry points (STN-72). Ids are in the
 * caller's id space — they become the emitted container node ids verbatim.
 */
export interface GroupLayoutSpec {
  /** Final container node id. Must not collide with a member node id. */
  id: string;
  /** Container label rendered in the group header. */
  label?: string;
  /** Candidate member ids; unknown ids and ids claimed by an earlier group are skipped. */
  nodeIds: string[];
}

export interface LayoutResult {
  nodes: Node[];
  edges: Edge[];
  groupRects: Map<string, GroupRect>;
}

// Container chrome: room for the dashed border and the header label strip.
export const GROUP_PAD = 12;
export const GROUP_PAD_TOP = 32;

// ---------------------------------------------------------------------------
// Dagre fallbacks (moved from pages/Editor — unchanged behavior)
// ---------------------------------------------------------------------------

export const getLayoutedElements = (
  nodes: Node[],
  edges: Edge[],
  direction: LayoutDirection = 'TB'
): FlatLayoutResult => {
  const isHorizontal = direction === 'LR';
  // A fresh graph per call — a module-level singleton accumulates stale nodes/edges
  // across generations, which silently corrupts later layouts.
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: direction });

  nodes.forEach((node) => {
    // We approximate the node dimensions here.
    // TechNode is roughly 200px wide, 80px high
    dagreGraph.setNode(node.id, { width: NODE_W, height: NODE_H });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      targetPosition: isHorizontal ? Position.Left : Position.Top,
      sourcePosition: isHorizontal ? Position.Right : Position.Bottom,
      // We are shifting the dagre node position (anchor=center center) to the top left
      // so it matches the React Flow node anchor point (top left).
      position: {
        x: nodeWithPosition.x - NODE_W / 2,
        y: nodeWithPosition.y - NODE_H / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

/**
 * Layout that keeps each group's members together (compound dagre) and returns the
 * bounding rect of each group's cluster, so AI-generated groups can be drawn as
 * container boxes. Falls back to the flat layout if anything goes wrong.
 */
const getLayoutedElementsWithGroups = (
  nodes: Node[],
  edges: Edge[],
  groups: GroupLayoutSpec[],
  direction: LayoutDirection = 'TB'
): LayoutResult => {
  const g = new dagre.graphlib.Graph({ compound: true });
  g.setGraph({ rankdir: direction });
  g.setDefaultEdgeLabel(() => ({}));

  const nodeIds = new Set(nodes.map((n) => n.id));
  nodes.forEach((node) => g.setNode(node.id, { width: NODE_W, height: NODE_H }));

  const cid = (gid: string) => `__cluster_${gid}`;
  const claimed = new Set<string>();
  const validGroups = groups.filter((gr) => gr.nodeIds.some((id) => nodeIds.has(id)));
  for (const gr of validGroups) {
    g.setNode(cid(gr.id), {});
    for (const memberId of gr.nodeIds) {
      // A node can only belong to one parent; first group wins.
      if (nodeIds.has(memberId) && !claimed.has(memberId)) {
        g.setParent(memberId, cid(gr.id));
        claimed.add(memberId);
      }
    }
  }

  edges.forEach((edge) => {
    if (nodeIds.has(edge.source) && nodeIds.has(edge.target)) g.setEdge(edge.source, edge.target);
  });

  dagre.layout(g);

  const isHorizontal = direction === 'LR';
  const layoutedNodes = nodes.map((node) => {
    const p = g.node(node.id);
    return {
      ...node,
      targetPosition: isHorizontal ? Position.Left : Position.Top,
      sourcePosition: isHorizontal ? Position.Right : Position.Bottom,
      position: { x: p.x - NODE_W / 2, y: p.y - NODE_H / 2 },
    };
  });

  // Dagre's cluster dimensions carry constant slack (25px left / 35px top),
  // so containers come out oversized. Recompute tight member bboxes like the
  // other layout paths do.
  const groupRects = computeGroupRects(
    validGroups,
    new Map(layoutedNodes.map((n) => [n.id, n.position])),
    new Map(layoutedNodes.map((n) => [n.id, { width: NODE_W, height: NODE_H }]))
  );

  return { nodes: layoutedNodes, edges, groupRects };
};

// ---------------------------------------------------------------------------
// Semantic placement
// ---------------------------------------------------------------------------

interface SanitizedHints {
  layer: number;
  orderInLayer: number;
  mainPath: boolean;
}

/**
 * Read a node's layout hints off its React Flow data payload. The materializer
 * stores the GeneratedNode fields it needs on data; layout hints ride along.
 */
function readHints(data: Record<string, unknown>): SanitizedHints | null {
  const raw = data?.layout as GeneratedStack['nodes'][number]['layout'] | undefined;
  if (!raw) return null;
  const layer = Number(raw.layer);
  const orderInLayer = Number(raw.orderInLayer);
  if (!Number.isFinite(layer) || layer < 0) return null;
  return {
    layer: Math.floor(layer),
    orderInLayer: Number.isFinite(orderInLayer) && orderInLayer >= 0 ? Math.floor(orderInLayer) : 0,
    mainPath: Boolean(raw.mainPath),
  };
}

/**
 * Hints are usable when a majority of tech nodes carry a sane layer and the
 * layers aren't all identical (all-zero layers carry no spatial information).
 * Sticky/text notes never carry hints and don't count against the threshold.
 */
export function hasUsableLayoutHints(nodes: Node[]): boolean {
  const techNodes = nodes.filter((n) => n.type === 'techNode');
  if (techNodes.length === 0) return false;
  const hinted = techNodes
    .map((n) => readHints(n.data as Record<string, unknown>))
    .filter(Boolean) as SanitizedHints[];
  if (hinted.length === 0 || hinted.length < techNodes.length * 0.6) return false;
  return new Set(hinted.map((h) => h.layer)).size > 1;
}

/**
 * Deterministic placement from model hints:
 * - Layer L sits L * LAYER_STEP along the flow axis.
 * - Within a layer, the main-path node takes cross coordinate 0 (so the spine
 *   is a straight line top-to-bottom / left-to-right) and side nodes fan out
 *   symmetrically at +/- SLOT_STEP.
 * - Group members are kept adjacent within a layer so group boxes stay tight.
 */
function placeSemantically(
  nodes: Node[],
  groups: GroupLayoutSpec[],
  direction: LayoutDirection
): { positions: Map<string, { x: number; y: number }>; hintedIds: Set<string> } {
  const horizontal = direction === 'LR' || direction === 'RL';
  const reverse = direction === 'BT' || direction === 'RL';

  // Group adjacency: node id -> first group id (by encounter order).
  const nodeToGroup = new Map<string, string>();
  for (const gr of groups) {
    for (const memberId of gr.nodeIds) {
      if (!nodeToGroup.has(memberId)) nodeToGroup.set(memberId, gr.id);
    }
  }

  const entries = nodes.map((n) => ({ node: n, hints: readHints(n.data as Record<string, unknown>) }));
  const hinted = entries.filter((e) => e.hints) as { node: Node; hints: SanitizedHints }[];
  const unhinted = entries.filter((e) => !e.hints).map((e) => e.node);

  const maxLayer = hinted.reduce((m, e) => Math.max(m, e.hints.layer), 0);
  // Bucket by layer.
  const byLayer = new Map<number, { node: Node; hints: SanitizedHints }[]>();
  for (const e of hinted) {
    const list = byLayer.get(e.hints.layer) ?? [];
    list.push(e);
    byLayer.set(e.hints.layer, list);
  }

  const positions = new Map<string, { x: number; y: number }>();
  const hintedIds = new Set<string>();

  const assign = (id: string, along: number, cross: number) => {
    // along/cross are center coordinates; convert to top-left.
    const cx = horizontal ? along : cross;
    const cy = horizontal ? cross : along;
    positions.set(id, { x: cx - NODE_W / 2, y: cy - NODE_H / 2 });
  };

  for (const [layer, members] of byLayer) {
    // Spine first, then group-adjacent ordering for the ribs.
    const spine = members.filter((m) => m.hints.mainPath);
    const ribs = members
      .filter((m) => !m.hints.mainPath)
      .sort((a, b) => a.hints.orderInLayer - b.hints.orderInLayer)
      // Stable partition by group so same-group nodes stay adjacent.
      .sort((a, b) => {
        const ga = nodeToGroup.get(a.node.id);
        const gb = nodeToGroup.get(b.node.id);
        if (ga === gb) return 0;
        if (!ga) return 1;
        if (!gb) return -1;
        return ga.localeCompare(gb);
      });

    const ordered = [...spine, ...ribs];
    // First spine node (if any) anchors cross 0; everything else fans out.
    let ribIndex = 0;
    ordered.forEach((m, i) => {
      const alongStep = reverse ? maxLayer - layer : layer;
      const along = alongStep * (horizontal ? SLOT_STEP : LAYER_STEP);
      const isAnchor = i === 0 && m.hints.mainPath;
      if (isAnchor) {
        assign(m.node.id, along, 0);
      } else {
        // Alternate right/left of the spine.
        const sign = ribIndex % 2 === 0 ? 1 : -1;
        const magnitude = Math.ceil((ribIndex + 1) / 2);
        const cross = sign * magnitude * (horizontal ? LAYER_STEP : SLOT_STEP);
        assign(m.node.id, along, cross);
        ribIndex++;
      }
      hintedIds.add(m.node.id);
    });
  }

  // Unhinted nodes (sticky notes, text nodes, stragglers): stack them in a
  // column to the right of the widest layer instead of piling at the origin.
  if (unhinted.length > 0) {
    let maxX = 0;
    let minY = Infinity;
    for (const p of positions.values()) {
      maxX = Math.max(maxX, p.x + NODE_W);
      minY = Math.min(minY, p.y);
    }
    if (!Number.isFinite(minY)) minY = 0;
    const railX = maxX + SLOT_STEP * 0.75;
    unhinted.forEach((n, i) => {
      const h = (n.style?.height as number) || n.height || 160;
      const y = minY + i * (h + 40);
      positions.set(n.id, { x: railX, y });
    });
  }

  return { positions, hintedIds };
}

/** Tight bounding box (top-left, size) of a group's placed members. */
function computeGroupRects(
  groups: GroupLayoutSpec[],
  positions: Map<string, { x: number; y: number }>,
  nodeSizes: Map<string, { width: number; height: number }>
): Map<string, GroupRect> {
  const rects = new Map<string, GroupRect>();
  for (const gr of groups) {
    const members = gr.nodeIds.filter((id) => positions.has(id));
    if (members.length === 0) continue;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const id of members) {
      const p = positions.get(id)!;
      const size = nodeSizes.get(id) ?? { width: NODE_W, height: NODE_H };
      minX = Math.min(minX, p.x);
      minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x + size.width);
      maxY = Math.max(maxY, p.y + size.height);
    }
    rects.set(gr.id, { x: minX, y: minY, width: maxX - minX, height: maxY - minY });
  }
  return rects;
}

// ---------------------------------------------------------------------------
// Group containers — React Flow parentNode sub-flows (STN-72)
// ---------------------------------------------------------------------------

/**
 * Turn a laid-out node set (absolute canvas positions) plus group specs into
 * React Flow parentNode sub-flows: each group becomes a `groupNode` container
 * sized to its members' padded bounding box, members are re-parented with
 * `extent: 'parent'` and coordinates relative to the container, and
 * containers precede their children in the array (React Flow requirement).
 *
 * Degradation rules: groups with no placed members, groups whose id collides
 * with a real node id, and member ids claimed by an earlier group (or absent
 * from `nodes`) are skipped — skipped members simply keep absolute
 * coordinates. Deterministic: output depends only on the inputs.
 */
export function withGroupContainers(
  nodes: Node[],
  groups: GroupLayoutSpec[],
  groupRects: Map<string, GroupRect>
): Node[] {
  if (groups.length === 0) return nodes;

  const idSet = new Set(nodes.map((n) => n.id));
  const claimed = new Set<string>();
  const containers: Node[] = [];
  const memberOf = new Map<string, { id: string; x: number; y: number }>();

  for (const gr of groups) {
    // A container id that duplicates a real node would corrupt the canvas —
    // leave that group flat instead.
    if (idSet.has(gr.id)) continue;
    const rect = groupRects.get(gr.id);
    if (!rect) continue;
    const members = gr.nodeIds.filter((id) => idSet.has(id) && !claimed.has(id));
    if (members.length === 0) continue;

    const x = rect.x - GROUP_PAD;
    const y = rect.y - GROUP_PAD_TOP;
    for (const memberId of members) {
      claimed.add(memberId);
      memberOf.set(memberId, { id: gr.id, x, y });
    }
    containers.push({
      id: gr.id,
      type: 'groupNode',
      position: { x, y },
      zIndex: -1,
      style: {
        width: rect.width + GROUP_PAD * 2,
        height: rect.height + GROUP_PAD_TOP + GROUP_PAD,
      },
      data: { label: gr.label ?? gr.id },
    });
  }

  if (containers.length === 0) return nodes;

  const rest = nodes.map((node) => {
    const parent = memberOf.get(node.id);
    if (!parent) return node;
    return {
      ...node,
      parentNode: parent.id,
      extent: 'parent' as const,
      position: { x: node.position.x - parent.x, y: node.position.y - parent.y },
    };
  });
  return [...containers, ...rest];
}

/**
 * Flatten parentNode sub-flows back to absolute canvas coordinates: strips
 * `parentNode`/`extent` from every node and resolves each position through
 * its parent chain. The inverse of {@link withGroupContainers} — callers that
 * re-layout an existing grouped canvas flatten first, then pass fresh group
 * specs to the layout entry points.
 */
export function toAbsolutePositions(nodes: Node[]): Node[] {
  const rects = absoluteRects(nodes);
  return nodes.map((node) => {
    if (typeof node.parentNode !== 'string') return node;
    const rect = rects.get(node.id);
    if (!rect) return { ...node, parentNode: undefined, extent: undefined };
    return {
      ...node,
      parentNode: undefined,
      extent: undefined,
      position: { x: rect.x, y: rect.y },
    };
  });
}

// ---------------------------------------------------------------------------
// Placement scoring — edge-length + crossing penalties (STN-74)
// ---------------------------------------------------------------------------

/**
 * Cost weights for placement scoring (STN-74).
 *
 * Tuned so authored hints (layer/orderInLayer/mainPath) always dominate:
 * a single hint-order violation (10_000) outweighs any edge-length or
 * crossing saving, so back-compat pinned layouts are unchanged. A crossing
 * (800) outweighs several slot-steps of length (0.5 per px ≈ 165 per slot),
 * so the optimiser prefers a nearby slot over a long jump even if it adds a
 * little length, but a hint pin still wins over both.
 *
 * Deterministic pure numbers — no randomness, no wall-clock.
 */
export const PLACEMENT_EDGE_LENGTH_WEIGHT = 0.5;
export const PLACEMENT_CROSSING_WEIGHT = 800;
export const PLACEMENT_HINT_VIOLATION_WEIGHT = 10_000;

export interface PlacementCostBreakdown {
  /** Sum of Euclidean centre-to-centre edge lengths (px). */
  edgeLength: number;
  /** Weighted edge-length penalty. */
  edgeLengthPenalty: number;
  /** Number of edge-edge crossings (pairs sharing no endpoint that intersect). */
  crossingCount: number;
  /** Weighted crossing penalty. */
  crossingPenalty: number;
  /** Number of hint-order inversions within ranks. */
  hintViolationCount: number;
  /** Weighted hint violation penalty. */
  hintViolationPenalty: number;
  /** Total cost — lower is better. */
  total: number;
}

function centreOf(pos: { x: number; y: number }): { x: number; y: number } {
  return { x: pos.x + NODE_W / 2, y: pos.y + NODE_H / 2 };
}

/** Sum of Euclidean centre-to-centre lengths for the given edge set. */
export function edgeLengthForPositions(
  positions: Map<string, { x: number; y: number }>,
  edges: Array<{ source: string; target: string }>
): number {
  let sum = 0;
  for (const e of edges) {
    const a = positions.get(e.source);
    const b = positions.get(e.target);
    if (!a || !b) continue;
    const ca = centreOf(a);
    const cb = centreOf(b);
    const dx = ca.x - cb.x;
    const dy = ca.y - cb.y;
    sum += Math.sqrt(dx * dx + dy * dy);
  }
  return sum;
}

function orientation(ax: number, ay: number, bx: number, by: number, cx: number, cy: number): number {
  const v = (by - ay) * (cx - bx) - (bx - ax) * (cy - by);
  if (v === 0) return 0;
  return v > 0 ? 1 : 2;
}

function onSegment(ax: number, ay: number, bx: number, by: number, cx: number, cy: number): boolean {
  return bx >= Math.min(ax, cx) && bx <= Math.max(ax, cx) && by >= Math.min(ay, cy) && by <= Math.max(ay, cy);
}

function segmentsIntersect(
  p1: { x: number; y: number },
  p2: { x: number; y: number },
  q1: { x: number; y: number },
  q2: { x: number; y: number }
): boolean {
  const o1 = orientation(p1.x, p1.y, p2.x, p2.y, q1.x, q1.y);
  const o2 = orientation(p1.x, p1.y, p2.x, p2.y, q2.x, q2.y);
  const o3 = orientation(q1.x, q1.y, q2.x, q2.y, p1.x, p1.y);
  const o4 = orientation(q1.x, q1.y, q2.x, q2.y, p2.x, p2.y);
  if (o1 !== o2 && o3 !== o4) return true;
  if (o1 === 0 && onSegment(p1.x, p1.y, q1.x, q1.y, p2.x, p2.y)) return true;
  if (o2 === 0 && onSegment(p1.x, p1.y, q2.x, q2.y, p2.x, p2.y)) return true;
  if (o3 === 0 && onSegment(q1.x, q1.y, p1.x, p1.y, q2.x, q2.y)) return true;
  if (o4 === 0 && onSegment(q1.x, q1.y, p2.x, p2.y, q2.x, q2.y)) return true;
  return false;
}

/**
 * Count edge-edge crossings (unordered pairs sharing no endpoint whose
 * straight segments intersect). Deterministic: edges are considered in
 * sorted order by source/target to keep the count stable.
 */
export function countEdgeCrossings(
  positions: Map<string, { x: number; y: number }>,
  edges: Array<{ source: string; target: string }>
): number {
  const sorted = [...edges].sort((a, b) => {
    const c = a.source.localeCompare(b.source);
    return c !== 0 ? c : a.target.localeCompare(b.target);
  });
  let crossings = 0;
  for (let i = 0; i < sorted.length; i++) {
    const e1 = sorted[i];
    const a1 = positions.get(e1.source);
    const b1 = positions.get(e1.target);
    if (!a1 || !b1) continue;
    const ca1 = centreOf(a1);
    const cb1 = centreOf(b1);
    for (let j = i + 1; j < sorted.length; j++) {
      const e2 = sorted[j];
      if (e1.source === e2.source || e1.source === e2.target || e1.target === e2.source || e1.target === e2.target) continue;
      const a2 = positions.get(e2.source);
      const b2 = positions.get(e2.target);
      if (!a2 || !b2) continue;
      const ca2 = centreOf(a2);
      const cb2 = centreOf(b2);
      if (segmentsIntersect(ca1, cb1, ca2, cb2)) crossings++;
    }
  }
  return crossings;
}

/**
 * Count hint-order inversions: within each rank, any pair where the
 * earlier node's orderInLayer is greater than a later node's counts as a
 * violation. This keeps the optimiser from reordering hint-pinned sequences.
 */
function hintViolationCountForOrdering(
  orderedByRank: Map<number, Node[]>,
  hintsOf: Map<string, SanitizedHints | null>
): number {
  let violations = 0;
  for (const list of orderedByRank.values()) {
    for (let i = 0; i < list.length; i++) {
      const oi = hintsOf.get(list[i].id)?.orderInLayer ?? 0;
      for (let j = i + 1; j < list.length; j++) {
        const oj = hintsOf.get(list[j].id)?.orderInLayer ?? 0;
        if (oi > oj) violations++;
      }
    }
  }
  return violations;
}

/**
 * Full placement cost breakdown — edge-length penalty + crossing penalty +
 * hint-violation penalty. Pure and deterministic; identical input produces
 * identical output. Lower total is better.
 */
export function placementCost(
  positions: Map<string, { x: number; y: number }>,
  edges: Array<{ source: string; target: string }>,
  orderedByRank?: Map<number, Node[]>,
  hintsOf?: Map<string, SanitizedHints | null>
): PlacementCostBreakdown {
  const edgeLength = edgeLengthForPositions(positions, edges);
  const edgeLengthPenalty = edgeLength * PLACEMENT_EDGE_LENGTH_WEIGHT;
  const crossingCount = countEdgeCrossings(positions, edges);
  const crossingPenalty = crossingCount * PLACEMENT_CROSSING_WEIGHT;
  const hintViolationCount = orderedByRank && hintsOf ? hintViolationCountForOrdering(orderedByRank, hintsOf) : 0;
  const hintViolationPenalty = hintViolationCount * PLACEMENT_HINT_VIOLATION_WEIGHT;
  return {
    edgeLength,
    edgeLengthPenalty,
    crossingCount,
    crossingPenalty,
    hintViolationCount,
    hintViolationPenalty,
    total: edgeLengthPenalty + crossingPenalty + hintViolationPenalty,
  };
}

/**
 * Convenience alias — total cost only. Exported for tests that assert
 * "long jump scores worse than nearby placement".
 */
export function scorePlacement(
  positions: Map<string, { x: number; y: number }>,
  edges: Array<{ source: string; target: string }>,
  orderedByRank?: Map<number, Node[]>,
  hintsOf?: Map<string, SanitizedHints | null>
): number {
  return placementCost(positions, edges, orderedByRank, hintsOf).total;
}

// ---------------------------------------------------------------------------
// Layered hierarchical pass (STN-73)
// ---------------------------------------------------------------------------

/** Options for {@link hierarchicalLayout}. */
export interface HierarchicalLayoutOptions {
  /** Flow direction. Default 'TB'. 'BT'/'RL' mirror 'TB'/'LR'. */
  direction?: LayoutDirection;
  /**
   * Center-to-center distance between adjacent ranks along the flow axis.
   * Defaults preserve current spacing: LAYER_STEP for vertical flows,
   * SLOT_STEP for horizontal flows (the semantic placer swaps the constants
   * the same way because node extent differs per axis).
   */
  rankSeparation?: number;
  /**
   * Center-to-center distance between neighbouring nodes within a rank,
   * across the flow axis. Defaults: SLOT_STEP vertical, LAYER_STEP horizontal.
   */
  nodeSeparation?: number;
}

interface RankEdge {
  source: string;
  target: string;
  /** False once dropped as a cycle-breaking back edge. */
  active: boolean;
}

const positiveStep = (value: number | undefined, fallback: number): number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : fallback;

/**
 * Layered hierarchical layout for directed flow graphs (STN-73).
 *
 * Ranks come from the edges: longest-path layering over the graph with
 * cycles broken by a deterministic DFS. Authored `layer` hints pin a node's
 * rank and always win over the inferred one; `orderInLayer` orders nodes
 * within a rank, same-group nodes stay adjacent, and input array order is
 * the final tiebreak. Everything is derived from the input arrays (sorted
 * keys, never keyed-Map iteration order), so identical input produces
 * byte-identical output.
 *
 * Non-flow nodes (sticky notes, text nodes, group containers) never join a
 * rank — they rail to the right of the flow like the semantic placer rails
 * them. Isolated tech nodes join rank 0 so they stay part of the diagram.
 */
export function hierarchicalLayout(
  nodes: Node[],
  edges: Edge[],
  groups: GroupLayoutSpec[] = [],
  options: HierarchicalLayoutOptions = {}
): LayoutResult {
  const direction = options.direction ?? 'TB';
  const horizontal = direction === 'LR' || direction === 'RL';
  const reverse = direction === 'BT' || direction === 'RL';
  const rankStep = positiveStep(options.rankSeparation, horizontal ? SLOT_STEP : LAYER_STEP);
  const nodeStep = positiveStep(options.nodeSeparation, horizontal ? LAYER_STEP : SLOT_STEP);

  if (nodes.length === 0) return { nodes: [], edges, groupRects: new Map() };

  const idSet = new Set(nodes.map((n) => n.id));
  const inputIndex = new Map<string, number>();
  nodes.forEach((n, i) => {
    if (!inputIndex.has(n.id)) inputIndex.set(n.id, i);
  });

  // Deduped, well-formed edges only — self-loops and dangling endpoints
  // carry no ranking information.
  const key = (s: string, t: string) => `${s}\u0000${t}`;
  const edgeByKey = new Map<string, RankEdge>();
  const edgeList: RankEdge[] = [];
  for (const e of edges) {
    if (e.source === e.target || !idSet.has(e.source) || !idSet.has(e.target)) continue;
    const k = key(e.source, e.target);
    if (edgeByKey.has(k)) continue;
    const re: RankEdge = { source: e.source, target: e.target, active: true };
    edgeByKey.set(k, re);
    edgeList.push(re);
  }

  const succ = new Map<string, string[]>();
  for (const e of edgeList) {
    const list = succ.get(e.source);
    if (list) list.push(e.target);
    else succ.set(e.source, [e.target]);
  }

  // Flow participants: every node an edge touches, plus tech-node isolates.
  const connected = new Set<string>();
  for (const e of edgeList) {
    connected.add(e.source);
    connected.add(e.target);
  }
  const participants: Node[] = [];
  const railed: Node[] = [];
  for (const n of nodes) {
    if (connected.has(n.id) || n.type === 'techNode') participants.push(n);
    else railed.push(n);
  }

  // Break cycles: DFS in input order, drop back edges from the ranking.
  const color = new Map<string, number>(); // 0 = white, 1 = gray, 2 = black
  for (const n of participants) color.set(n.id, 0);
  for (const root of participants) {
    if (color.get(root.id) !== 0) continue;
    color.set(root.id, 1);
    const stack: Array<{ id: string; next: number }> = [{ id: root.id, next: 0 }];
    while (stack.length > 0) {
      const top = stack[stack.length - 1];
      const neighbors = succ.get(top.id) ?? [];
      if (top.next < neighbors.length) {
        const v = neighbors[top.next++];
        const c = color.get(v);
        if (c === 1) {
          const re = edgeByKey.get(key(top.id, v));
          if (re) re.active = false;
        } else if (c === 0) {
          color.set(v, 1);
          stack.push({ id: v, next: 0 });
        }
      } else {
        color.set(top.id, 2);
        stack.pop();
      }
    }
  }

  // Rank: authored layer hints pin the rank; everything else relaxes to the
  // longest path (rank(target) >= rank(source) + 1) over the acyclic edge set.
  const hintsOf = new Map<string, SanitizedHints | null>();
  const rank = new Map<string, number>();
  const pinned = new Set<string>();
  for (const n of participants) {
    const h = readHints(n.data as Record<string, unknown>);
    hintsOf.set(n.id, h);
    if (h) {
      rank.set(n.id, h.layer);
      pinned.add(n.id);
    } else {
      rank.set(n.id, 0);
    }
  }
  let changed = true;
  let rounds = 0;
  while (changed && rounds <= participants.length + 1) {
    changed = false;
    rounds++;
    for (const e of edgeList) {
      if (!e.active || pinned.has(e.target)) continue;
      const next = rank.get(e.source)! + 1;
      if (next > rank.get(e.target)!) {
        rank.set(e.target, next);
        changed = true;
      }
    }
  }

  let maxRank = 0;
  for (const n of participants) maxRank = Math.max(maxRank, rank.get(n.id)!);

  // Same-group nodes stay adjacent within a rank (first group wins, by
  // encounter order — same rule as the semantic placer).
  const groupIndexOf = new Map<string, number>();
  groups.forEach((g, i) => {
    for (const m of g.nodeIds) {
      if (idSet.has(m) && !groupIndexOf.has(m)) groupIndexOf.set(m, i);
    }
  });

  const byRank = new Map<number, Node[]>();
  for (const n of participants) {
    const r = rank.get(n.id)!;
    const list = byRank.get(r) ?? [];
    list.push(n);
    byRank.set(r, list);
  }
  const sortMember = (a: Node, b: Node): number => {
    const ga = groupIndexOf.get(a.id) ?? groups.length;
    const gb = groupIndexOf.get(b.id) ?? groups.length;
    if (ga !== gb) return ga - gb;
    const oa = hintsOf.get(a.id)?.orderInLayer ?? 0;
    const ob = hintsOf.get(b.id)?.orderInLayer ?? 0;
    if (oa !== ob) return oa - ob;
    return (inputIndex.get(a.id) ?? 0) - (inputIndex.get(b.id) ?? 0);
  };

  const rankValues = [...byRank.keys()].sort((a, b) => a - b);
  // Build mutable ordered lists per rank, seeded by the hint-respecting sort.
  const orderedByRank = new Map<number, Node[]>();
  for (const r of rankValues) {
    orderedByRank.set(r, [...(byRank.get(r) ?? [])].sort(sortMember));
  }

  const computePositions = (ordering: Map<number, Node[]>): Map<string, { x: number; y: number }> => {
    const pos = new Map<string, { x: number; y: number }>();
    for (const r of rankValues) {
      const members = ordering.get(r) ?? [];
      const along = (reverse ? maxRank - r : r) * rankStep;
      members.forEach((m, i) => {
        const cross = (i - (members.length - 1) / 2) * nodeStep;
        const cx = horizontal ? along : cross;
        const cy = horizontal ? cross : along;
        pos.set(m.id, { x: cx - NODE_W / 2, y: cy - NODE_H / 2 });
      });
    }
    return pos;
  };

  let positions = computePositions(orderedByRank);
  let bestCost = placementCost(positions, edgeList, orderedByRank, hintsOf).total;

  // Deterministic hill-climbing: try adjacent swaps within each rank.
  // Only swaps that keep hint order (same orderInLayer & same group) are
  // considered, so pinned layouts stay unchanged and hints always win over
  // the edge-length / crossing penalties. Pure — no randomness or wall-clock.
  const MAX_OPT_PASSES = 8;
  let pass = 0;
  let improved = true;
  while (improved && pass < MAX_OPT_PASSES) {
    improved = false;
    pass++;
    for (const r of rankValues) {
      const list = orderedByRank.get(r)!;
      if (list.length < 2) continue;
      for (let i = 0; i < list.length - 1; i++) {
        const a = list[i];
        const b = list[i + 1];
        const ga = groupIndexOf.get(a.id) ?? groups.length;
        const gb = groupIndexOf.get(b.id) ?? groups.length;
        if (ga !== gb) continue;
        const oa = hintsOf.get(a.id)?.orderInLayer ?? 0;
        const ob = hintsOf.get(b.id)?.orderInLayer ?? 0;
        if (oa !== ob) continue;
        // Try swap.
        const tmp = list[i];
        list[i] = list[i + 1];
        list[i + 1] = tmp;
        const trial = computePositions(orderedByRank);
        const trialCost = placementCost(trial, edgeList, orderedByRank, hintsOf).total;
        if (trialCost < bestCost - 1e-9) {
          positions = trial;
          bestCost = trialCost;
          improved = true;
        } else {
          // Revert.
          const tmp2 = list[i];
          list[i] = list[i + 1];
          list[i + 1] = tmp2;
        }
      }
    }
  }

  // Non-flow nodes (sticky notes, text nodes, empty containers) stack in a
  // rail to the right of the widest rank — same recipe as semantic placement.
  if (railed.length > 0) {
    let maxX = 0;
    let minY = Infinity;
    for (const p of positions.values()) {
      maxX = Math.max(maxX, p.x + NODE_W);
      minY = Math.min(minY, p.y);
    }
    if (!Number.isFinite(minY)) minY = 0;
    const railX = maxX + SLOT_STEP * 0.75;
    railed.forEach((n, i) => {
      const h = (n.style?.height as number) || n.height || 160;
      positions.set(n.id, { x: railX, y: minY + i * (h + 40) });
    });
  }

  const nodeSizes = new Map(
    nodes.map((n) => {
      const w = (n.style?.width as number) || n.width || NODE_W;
      const h = (n.style?.height as number) || n.height || NODE_H;
      return [n.id, { width: w, height: h }];
    })
  );
  const layoutedNodes = nodes.map((node) => ({
    ...node,
    targetPosition: horizontal ? Position.Left : Position.Top,
    sourcePosition: horizontal ? Position.Right : Position.Bottom,
    position: positions.get(node.id) ?? node.position,
  }));

  const groupRects = computeGroupRects(groups, positions, nodeSizes);
  return {
    nodes: withGroupContainers(layoutedNodes, groups, groupRects),
    edges,
    groupRects,
  };
}

// ---------------------------------------------------------------------------
// Deterministic handle derivation
// ---------------------------------------------------------------------------

export type HandleSide = 'top' | 'bottom' | 'left' | 'right';

export interface NodeRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Absolute bounding rects for a node set. Nodes parented to a group carry
 * positions relative to that parent — walk the chain so edge geometry sees
 * canvas coordinates.
 */
export function absoluteRects(nodes: Node[]): Map<string, NodeRect> {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const rects = new Map<string, NodeRect>();

  const resolve = (node: Node, seen = new Set<string>()): { x: number; y: number } => {
    let { x, y } = node.position;
    const parentId = typeof node.parentNode === 'string' ? node.parentNode : null;
    if (parentId && !seen.has(node.id)) {
      seen.add(node.id);
      const parent = byId.get(parentId);
      if (parent) {
        const abs = resolve(parent, seen);
        x += abs.x;
        y += abs.y;
      }
    }
    return { x, y };
  };

  for (const n of nodes) {
    const origin = resolve(n);
    rects.set(n.id, {
      ...origin,
      width: (n.style?.width as number) || n.width || NODE_W,
      height: (n.style?.height as number) || n.height || NODE_H,
    });
  }
  return rects;
}

/**
 * Bounding rect for export (PNG/SVG). Resolves parent-relative positions the
 * same way {@link absoluteRects} does, so a container at (500,300) with a
 * child at relative (20,40) is treated as absolute (520,340) rather than
 * (20,40). If a node already carries `positionAbsolute` (store node), that is
 * preferred — it is the source of truth when available.
 *
 * Returns `{x,y,width,height}` compatible with `getRectOfNodes` /
 * `getTransformForBounds`. Empty input yields a zero rect.
 */
export function getExportBounds(nodes: Node[]): { x: number; y: number; width: number; height: number } {
  if (nodes.length === 0) return { x: 0, y: 0, width: 0, height: 0 };
  const rects = absoluteRects(nodes);
  // Prefer positionAbsolute when present — mirrors the "take bounds from the
  // store" alternative mentioned in the audit. `positionAbsolute` already
  // includes the parent offset, so we override the resolved rect origin.
  for (const n of nodes) {
    const abs = (n as unknown as { positionAbsolute?: { x: number; y: number } }).positionAbsolute;
    if (abs && Number.isFinite(abs.x) && Number.isFinite(abs.y)) {
      const r = rects.get(n.id);
      if (r) {
        r.x = abs.x;
        r.y = abs.y;
      }
    }
  }
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const r of rects.values()) {
    minX = Math.min(minX, r.x);
    minY = Math.min(minY, r.y);
    maxX = Math.max(maxX, r.x + r.width);
    maxY = Math.max(maxY, r.y + r.height);
  }
  if (!Number.isFinite(minX) || !Number.isFinite(minY)) return { x: 0, y: 0, width: 0, height: 0 };
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
}

/** Pick the handle side pair for one source→target rect pair. */
function sidesForPair(
  source: NodeRect,
  target: NodeRect,
  direction: LayoutDirection = 'TB'
): { sourceHandle: HandleSide; targetHandle: HandleSide } {
  // Projection-based routing. If the rects don't share a band along the flow
  // axis, route along the flow — this is what keeps hub fans clean: rays from
  // the hub's bottom anchor to each target's top anchor stay above the
  // targets' shared top line, so they can't clip sibling targets (the
  // |dy|>=|dx| rule routed wide fans sideways and cut diagonals through
  // neighboring nodes). The band check order follows the flow direction: TB
  // checks Y first, LR/RL check X first — otherwise cross-column edges in an
  // LR diagram get vertical handles and drop straight through same-column
  // node stacks.
  const sharesYBand = source.y < target.y + target.height && target.y < source.y + source.height;
  const sharesXBand = source.x < target.x + target.width && target.x < source.x + source.width;

  const vertical = (): { sourceHandle: HandleSide; targetHandle: HandleSide } =>
    target.y >= source.y
      ? { sourceHandle: 'bottom', targetHandle: 'top' }
      : { sourceHandle: 'top', targetHandle: 'bottom' };
  const horizontal = (): { sourceHandle: HandleSide; targetHandle: HandleSide } =>
    target.x >= source.x
      ? { sourceHandle: 'right', targetHandle: 'left' }
      : { sourceHandle: 'left', targetHandle: 'right' };

  const horizontalFlow = direction === 'LR' || direction === 'RL';
  if (horizontalFlow ? !sharesXBand : !sharesYBand) {
    return horizontalFlow ? horizontal() : vertical();
  }
  if (horizontalFlow ? !sharesYBand : !sharesXBand) {
    return horizontalFlow ? vertical() : horizontal();
  }
  // Genuine overlap on both axes: dominant axis decides.
  const dx = target.x + target.width / 2 - (source.x + source.width / 2);
  const dy = target.y + target.height / 2 - (source.y + source.height / 2);
  if (Math.abs(dy) >= Math.abs(dx)) {
    return dy >= 0
      ? { sourceHandle: 'bottom', targetHandle: 'top' }
      : { sourceHandle: 'top', targetHandle: 'bottom' };
  }
  return dx >= 0
    ? { sourceHandle: 'right', targetHandle: 'left' }
    : { sourceHandle: 'left', targetHandle: 'right' };
}

/**
 * Derive sourceHandle/targetHandle for every edge from the FINAL laid-out node
 * geometry. This implements the Archify invariant "automatic routes own their
 * endpoint sides": model- or parser-supplied handle guesses are never trusted
 * — positions are the truth. Edges referencing unknown nodes are skipped and
 * keep whatever handles they had.
 */
export function deriveHandles(
  edges: Edge[],
  nodes: Node[],
  direction: LayoutDirection = 'TB'
): Map<string, { sourceHandle: HandleSide; targetHandle: HandleSide }> {
  const rects = absoluteRects(nodes);
  const result = new Map<string, { sourceHandle: HandleSide; targetHandle: HandleSide }>();

  for (const edge of edges) {
    const source = rects.get(edge.source);
    const target = rects.get(edge.target);
    if (!source || !target) continue;
    result.set(edge.id, sidesForPair(source, target, direction));
  }
  return result;
}

// ---------------------------------------------------------------------------
// Orchestrator
// ---------------------------------------------------------------------------

/**
 * Position a generated diagram. Prefers semantic placement when the model
 * provided usable layout hints; otherwise falls back to dagre (compound when
 * groups are present). Groups become React Flow parentNode sub-flows: each
 * group is emitted as a `groupNode` container sized to its members and
 * members carry `extent: 'parent'` positions relative to it. `groupRects`
 * still reports each container's absolute (unpadded) member bounding box.
 */
export function layoutElements(
  nodes: Node[],
  edges: Edge[],
  groups: GroupLayoutSpec[],
  direction: LayoutDirection = 'TB'
): LayoutResult {
  const semantic = nodes.length > 0 && hasUsableLayoutHints(nodes);

  if (semantic) {
    const { positions } = placeSemantically(nodes, groups, direction);
    const nodeSizes = new Map(
      nodes.map((n) => {
        const w = (n.style?.width as number) || n.width || NODE_W;
        const h = (n.style?.height as number) || n.height || NODE_H;
        return [n.id, { width: w, height: h }];
      })
    );
    const isHorizontal = direction === 'LR' || direction === 'RL';
    const layoutedNodes = nodes.map((node) => ({
      ...node,
      targetPosition: isHorizontal ? Position.Left : Position.Top,
      sourcePosition: isHorizontal ? Position.Right : Position.Bottom,
      position: positions.get(node.id) ?? node.position,
    }));
    const groupRects = computeGroupRects(groups, positions, nodeSizes);
    return { nodes: withGroupContainers(layoutedNodes, groups, groupRects), edges, groupRects };
  }

  if (groups.length > 0) {
    try {
      const result = getLayoutedElementsWithGroups(nodes, edges, groups, direction);
      return { ...result, nodes: withGroupContainers(result.nodes, groups, result.groupRects) };
    } catch (err) {
      console.warn('Group layout failed; using flat layout', err);
    }
  }
  return { ...getLayoutedElements(nodes, edges, direction), groupRects: new Map() };
}
